import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import SESSION_CONFIG from '../config/auth';

const AuthContext = createContext();

// Get timeout settings from config
const { 
  INACTIVITY_TIMEOUT, 
  WARNING_BEFORE_TIMEOUT, 
  ACTIVITY_EVENTS 
} = SESSION_CONFIG;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_BEFORE_TIMEOUT);
  
  const navigate = useNavigate();
  const userActivityTimeout = useRef(null);
  const sessionWarningTimeout = useRef(null);
  const lastActivity = useRef(Date.now());

  // Reset timers when there's user activity
  const resetActivityTimers = useCallback(() => {
    if (!user) return;
    
    lastActivity.current = Date.now();
    
    // Clear existing timeouts
    if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    if (sessionWarningTimeout.current) clearTimeout(sessionWarningTimeout.current);
    
    // Hide warning modal if it's showing
    setShowTimeoutWarning(false);
    
    // Set new timeouts
    sessionWarningTimeout.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeRemaining(WARNING_BEFORE_TIMEOUT);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);
    
    userActivityTimeout.current = setTimeout(() => {
      if (showTimeoutWarning) return; // If warning is showing, let it handle the timeout
      signOut(true); // Auto logout due to inactivity should pass expired=true
    }, INACTIVITY_TIMEOUT);
  }, [user, showTimeoutWarning]);

  // Extend session
  const extendSession = useCallback(async () => {
    if (!user) return;
    
    setIsExtendingSession(true);
    try {
      // Refresh token if possible
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      // Reset activity timers
      resetActivityTimers();
    } catch (error) {
      console.error('Error extending session:', error);
      // If refresh fails, log out
      signOut();
    } finally {
      setIsExtendingSession(false);
      setShowTimeoutWarning(false);
    }
  }, [user, resetActivityTimers]);
  
  // Handle user activity
  useEffect(() => {
    const handleUserActivity = () => {
      resetActivityTimers();
    };
    
    // Register event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    return () => {
      // Clean up event listeners
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [resetActivityTimers]);
  
  // Initialize authentication
  useEffect(() => {
    // Check for active session on initial load
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setUser(data?.session?.user || null);
        
        // If user is authenticated, start activity timers
        if (data?.session?.user) {
          resetActivityTimers();
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user || null);
        setLoading(false);
        
        if (session?.user) {
          resetActivityTimers();
        } else {
          // Clear timeouts if user logged out
          if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
          if (sessionWarningTimeout.current) clearTimeout(sessionWarningTimeout.current);
        }
      }
    );

    // Cleanup on unmount
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
      
      if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
      if (sessionWarningTimeout.current) clearTimeout(sessionWarningTimeout.current);
    };
  }, [resetActivityTimers]);
  
  // Handle session warning countdown
  useEffect(() => {
    let countdownInterval;
    
    if (showTimeoutWarning) {
      countdownInterval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1000) {
            clearInterval(countdownInterval);
            signOut(true); // Session expired
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }
    
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [showTimeoutWarning]);

  const signOut = async (sessionExpired = false) => {
    try {
      // Clear all timeouts
      if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
      if (sessionWarningTimeout.current) clearTimeout(sessionWarningTimeout.current);
      
      setShowTimeoutWarning(false);
      await supabase.auth.signOut();
      setUser(null);
      
      // Navigate to login with expired parameter if session expired
      if (sessionExpired) {
        navigate('/login?expired=true');
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    signOut,
    isAuthenticated: !!user,
    extendSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        open={showTimeoutWarning}
        onExtend={extendSession}
        onLogout={signOut}
        timeRemaining={timeRemaining}
        isExtending={isExtendingSession}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
} 